import * as JsonSchema from '../../utils/json-schema'

export type T = WatchApiResponse

type WatchApiResponse = {
  watchProcedureId: string,
  startsAt: string, // ISO8601 UTC date
  endsAt: string, // ISO8601 UTC date
  period: string
  rules: Rule[],
  targets: Target[]
}

type Target = {
  name: string,
  seriesId: string,
  subjectToRule: string,
  statusChanges: TargetStatusChange[],
  silences: Silence[], 
  displaySeriesId?:string,
  unit?:string
}

type Rule = {
  key: string,
  name: string,
  condition: Condition,
  delay: number
}

type Condition =
  | BelowThresholdCondition
  | AboveThresholdCondition
  | InsideRangeCondition

type BelowThresholdCondition = {
  type: 'BelowThreshold',
  threshold: number,
  unit?: string
}

type AboveThresholdCondition = {
  type: 'AboveThreshold',
  threshold: number,
  unit?: string
}

type InsideRangeCondition = {
  type: 'InsideRange',
  range: [number, number],
  unit?: string
}

type TargetStatusChange = { time: string, status: TargetStatus }

export type TargetStatus = 'Ok' | 'Pending' | 'Alert' | 'Unsteady'

type Silence = [string, string] // ISO8601 UTC dates

const BELOW_THRESHOLD_CONDITION_SCHEMA: JsonSchema.T<BelowThresholdCondition> = {
  type      : 'object',
  required  : [ 'type', 'threshold' ],
  properties: {
    type     : { type: 'string', 'const': 'BelowThreshold' },
    threshold: { type: 'number' },
    unit     : { type: 'string', nullable: true }
  }
}

const ABOVE_THRESHOLD_CONDITION_SCHEMA: JsonSchema.T<AboveThresholdCondition> = {
  type      : 'object',
  required  : [ 'type', 'threshold' ],
  properties: {
    type     : { type: 'string', 'const': 'AboveThreshold' },
    threshold: { type: 'number' },
    unit     : { type: 'string', nullable: true }
  }
}

const INSIDE_RANGE_CONDITION_SCHEMA: JsonSchema.T<InsideRangeCondition> = {
  type      : 'object',
  required  : [ 'type', 'range' ],
  properties: {
    type : { type: 'string', 'const': 'InsideRange' },
    range: {
      type    : 'array',
      minItems: 2,
      maxItems: 2,
      items   : [
        { type: 'number' },
        { type: 'number' }
      ]
    },
    unit: { type: 'string', nullable: true }
  }
}

const CONDITION_SCHEMA: JsonSchema.T<Condition> = {
  oneOf: [
    BELOW_THRESHOLD_CONDITION_SCHEMA,
    ABOVE_THRESHOLD_CONDITION_SCHEMA,
    INSIDE_RANGE_CONDITION_SCHEMA
  ]
}

const RULE_SCHEMA: JsonSchema.T<Rule> = {
  type      : 'object',
  required  : [ 'key', 'name', 'condition', 'delay' ],
  properties: {
    key      : { type: 'string' },
    name     : { type: 'string' },
    condition: CONDITION_SCHEMA,
    delay    : { type: 'number' }
  }
}

const SILENCE_SCHEMA: JsonSchema.T<Silence> = {
  type           : 'array',
  items          : [ { type: 'string', format: 'date-time' }, { type: 'string', format: 'date-time' } ],
  minItems       : 2,
  additionalItems: false
}

const TARGET_STATUS_SCHEMA: JsonSchema.T<TargetStatus> = { type: 'string', 'enum': [ 'Ok', 'Pending', 'Alert', 'Unsteady' ] }

const TARGET_STATUS_CHANGE_SCHEMA: JsonSchema.T<TargetStatusChange> = {
  type      : 'object',
  required  : [ 'time', 'status' ],
  properties: {
    time  : { type: 'string' },
    status: TARGET_STATUS_SCHEMA
  }
}

const TARGET_SCHEMA: JsonSchema.T<Target> = {
  type      : 'object',
  required  : [ 'name', 'seriesId', 'subjectToRule', 'statusChanges' ],
  properties: {
    name         : { type: 'string' },
    seriesId     : { type: 'string' },
    subjectToRule: { type: 'string' },
    statusChanges: {
      type : 'array',
      items: TARGET_STATUS_CHANGE_SCHEMA
    },
    displaySeriesId: { type: 'string', nullable: true },
    unit           : { type: 'string', nullable: true },
    silences       : { type: 'array', items: SILENCE_SCHEMA }
  }
}

const WATCH_API_RESPONSE_SCHEMA: JsonSchema.T<WatchApiResponse> = {
  type      : 'object',
  required  : [ 'watchProcedureId', 'startsAt', 'endsAt', 'period', 'rules', 'targets' ],
  properties: {
    watchProcedureId: { type: 'string' },
    period          : { type: 'string' },
    startsAt        : { type: 'string' }, // ISO8601 UTC date
    endsAt          : { type: 'string' }, // ISO8601 UTC date
    rules           : {
      type : 'array',
      items: RULE_SCHEMA
    },
    targets: {
      type : 'array',
      items: TARGET_SCHEMA
    }
  }
}

export const parse = JsonSchema.parserFor(WATCH_API_RESPONSE_SCHEMA)
